import React, {useEffect, useRef, useState} from 'react';
import {PaystackConsumer} from 'react-paystack';
import {toast} from 'react-toastify';
import {useMutation} from 'react-query';
import {infoGrey, infoRed, paystack, transfer} from '../assets/images/images';
import Modal from './Modal';
import PrimaryButton from './PrimaryButton';
import {connect} from 'react-redux';
import * as Actions from '../store/actions';
import StripeCheckoutForm from './StripeCheckoutForm';
import {Elements} from '@stripe/react-stripe-js';
import {api} from '../helpers';
import {formatNumberToCurrency} from '../helpers/formatNumberToCurrency';
import {appearance, stripePromise} from '../helpers/constants/stripeConstants';
import {shortenLargeNumbers} from '../helpers/shortenLargeNumbers';
import {ChevronRight, X} from 'lucide-react';
import {AmountInput} from '../routes/Settings/Billing/AmountTopUp/AmountInput';

export function makePayment(data) {
  return api.post('/payments/get_details/stripe', data).then(res => res.data);
}

function TopupModal({
  auth: {paymentDetails, wallet, loading, userDetails},
  verifyPayment,
  startPayment,
  getAccountNumber,
  ...otherProps
}) {
  const {open, setOpen} = otherProps;
  const [showPayment, setShowPayment] = useState('');
  const [amount, setAmount] = useState('');
  const [allowTransfer, setAllowTransfer] = useState(false);
  const [reference, setReference] = useState(null);
  const payButton = useRef(null);
  const [stripeReference, setStripeReference] = useState('');

  // stripe
  const [clientSecret, setClientSecret] = useState('');

  const {mutate: makePaymentMutation, isLoading: isPaymentLoading} =
    useMutation(makePayment, {
      onSuccess: data => {
        setClientSecret(data.clientSecret);
        setStripeReference(data.reference);
        setAmount('');
      },
      onError: error => {
        console.error('Error making payment:', error);
        toast.error('An error occurred while processing your payment.');
      },
    });

  const options = {
    clientSecret,
    appearance,
  };

  const onSuccess = ref => {
    setReference(ref.reference);
  };

  const onClose = () => {
    toast.error('Payment gateway Closed');
  };

  const handlePayment = () => {
    if (amount < 1000) {
      toast.error(
        `Amount must not be lower than ${formatNumberToCurrency(
          1,
          userDetails && userDetails?.company && userDetails?.company?.currency,
        )}`,
      );
      return;
    }
    const vatRate = 0.075; // 7.5% VAT
    const vatAmount = +amount * vatRate;
    const totalAmount = Number(amount) + vatAmount;

    startPayment({amount: totalAmount});
  };

  const componentProps = {
    email: paymentDetails && paymentDetails?.email,
    reference: paymentDetails && paymentDetails?.ref,
    amount: paymentDetails && paymentDetails?.amount,
    publicKey: paymentDetails && paymentDetails?.p_key,
    text: 'Pay Online',
    onSuccess,
    onClose,
  };

  useEffect(() => {
    if (reference) {
      verifyPayment({reference}).then(() => {
        setAmount('');
        setOpen(false);
      });
    }
  }, [reference, verifyPayment, setOpen]);

  useEffect(() => {
    const pay = async () => {
      if (payButton.current !== null) payButton.current.click();
    };

    pay();
  }, [paymentDetails]);

  const canProceed = ['paystack', 'transfer'].includes(showPayment)
    ? Number(amount) >= 50000
    : Number(amount) >= 50;

  if (showPayment === 'paystack') {
    return (
      <TopupModalWrapper
        headerText="Continue with paystack"
        open={open}
        setOpen={() => {
          setOpen(!open);
          setAllowTransfer(false);
          setAmount('');
        }}
        setShowPayment={setShowPayment}
      >
        <AmountInput
          amount={amount}
          setAmount={setAmount}
          showPayment={showPayment}
        />

        <div className="w-full px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
          <PrimaryButton
            loading={loading}
            onClick={handlePayment}
            buttonText="Continue"
            disabled={!canProceed}
            className="w-full !h-10"
          />
          <PaystackConsumer {...componentProps}>
            {({initializePayment}) => (
              <button
                ref={payButton}
                style={{display: 'none'}}
                disabled={amount < 1000}
                onClick={() => initializePayment()}
              >
                Pay Button
              </button>
            )}
          </PaystackConsumer>
        </div>
      </TopupModalWrapper>
    );
  } else if (showPayment === 'transfer') {
    return (
      <TopupModalWrapper
        headerText="Pay via transfer"
        open={open}
        setOpen={() => {
          setOpen(!open);
          setAllowTransfer(false);
          setAmount('');
        }}
        setShowPayment={setShowPayment}
      >
        {!allowTransfer && (
          <>
            <AmountInput
              amount={amount}
              setAmount={setAmount}
              showPayment={showPayment}
            />

            <div className="w-full px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
              <PrimaryButton
                loading={loading}
                onClick={() => {
                  wallet ? null : getAccountNumber();
                  setAllowTransfer(true);
                }}
                buttonText="Continue"
                disabled={!canProceed}
                className="w-full !h-10"
              />
            </div>
          </>
        )}
        {wallet && allowTransfer && (
          <div className="flex flex-col py-4 px-6 gap-1 mx-auto max-w-[206px] font-medium">
            <span className="text-sm text-[#344272] font-light">
              Make payments to
            </span>
            <span className="text-xl text-grey my-1">
              {wallet?.entity?.account_number || '0000000000'}
            </span>
            <span className="text-xl text-grey">
              {wallet?.entity?.bank_name || 'Bank Name'}
            </span>
          </div>
        )}

        {!wallet && !loading && allowTransfer && (
          <p className="my-0 text-center fw-medium py-4 px-6">
            {userDetails?.company.verified ? (
              <>
                Unable to fetch account number.{' '}
                <span
                  className="small btn-light pointer text-underline"
                  onClick={() => getAccountNumber()}
                >
                  Try again
                </span>
              </>
            ) : (
              'Company must be verified to create a wallet'
            )}
          </p>
        )}
        <div className="w-full px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
          {allowTransfer && (
            <PrimaryButton
              buttonText="I have made the transfer"
              loading={loading}
              onClick={() => window.location.reload()}
              disabled={!userDetails?.company?.verified}
              className="w-full !h-10"
            />
          )}
        </div>
      </TopupModalWrapper>
    );
  } else if (showPayment === 'stripe') {
    return (
      <TopupModalWrapper
        headerText="Pay with Stripe"
        open={open}
        setOpen={() => {
          setOpen(!open);
          setAllowTransfer(false);
          setAmount('');
        }}
        setShowPayment={setShowPayment}
        setClientSecret={setClientSecret}
      >
        {clientSecret ? (
          <Elements options={options} stripe={stripePromise}>
            <StripeCheckoutForm setOpen={setOpen} reference={stripeReference} />
          </Elements>
        ) : (
          <>
            <div className="py-4 px-6">
              <label htmlFor="topup_amount">
                <span className="text-sm ">Enter amount</span>
                <input
                  placeholder="$0.00"
                  className="mt-2"
                  id="amount"
                  value={amount}
                  type="number"
                  onChange={e => {
                    setAmount(e.target.value);
                  }}
                />
              </label>
              <div className="flex items-center gap-1 mt-2">
                <img
                  src={amount && canProceed ? infoGrey : infoRed}
                  alt=""
                  width={16}
                />
                <p
                  className={`${
                    amount && canProceed ? 'text-grey-500' : 'text-status-404'
                  } text-sm`}
                >
                  The minimum top-up amount is{' '}
                  {shortenLargeNumbers(50, 'USD', {startAt: 'B'})}
                </p>
              </div>
            </div>

            <div className="w-full px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
              <PrimaryButton
                loading={isPaymentLoading}
                buttonText="Continue"
                className="w-full !h-10"
                onClick={() => {
                  makePaymentMutation({
                    amount: amount,
                    currency: userDetails?.company?.currency,
                  });
                }}
                disabled={!canProceed}
              />
            </div>
          </>
        )}
      </TopupModalWrapper>
    );
  } else
    return (
      <TopupModalWrapper
        setShowPayment={setShowPayment}
        open={open}
        setOpen={setOpen}
        headerText="Top up"
      >
        {userDetails?.company?.currency === 'USD' ? (
          <>
            <button
              className="p-6 mt-2 flex items-center justify-between w-full border-[0.5px] border-transparent border-b-[#EFEFEF]"
              onClick={() => {
                setShowPayment('stripe');
              }}
            >
              <div className="flex items-center gap-4">
                <img src={transfer} alt="" width={24} height={24} />
                <span className="text-base font-medium text-grey">
                  Pay with Stripe
                </span>
              </div>
              <ChevronRight color="#84858D" size={20} />
            </button>
            <div className="h-7 w-full border-[0.5px] border-transparent border-t-[#EFEFEF]"></div>
          </>
        ) : (
          <>
            <button
              className="p-6 mt-2 flex items-center justify-between w-full border-[0.5px] border-transparent border-b-[#EFEFEF]"
              onClick={() => {
                setShowPayment('transfer');
              }}
            >
              {' '}
              <div className="flex items-center gap-4">
                <img src={transfer} alt="" width={24} height={24} />
                <span className="text-base font-medium text-grey">
                  Pay via transfer
                </span>
              </div>
              <ChevronRight color="#84858D" size={20} />
            </button>
            <button
              className="p-6 mt-2 flex items-center justify-between w-full border-[0.5px] border-transparent border-b-[#EFEFEF]"
              onClick={() => setShowPayment('paystack')}
            >
              <div className="flex items-center gap-4">
                <img src={paystack} alt="" width={24} height={24} />
                <span className="text-base font-medium text-grey">
                  Continue with Paystack
                </span>
              </div>
              <ChevronRight color="#84858D" size={20} />
            </button>
            <div className="h-7 w-full border-[0.5px] border-transparent border-t-[#EFEFEF]"></div>
          </>
        )}
      </TopupModalWrapper>
    );
}

function TopupModalWrapper({
  headerText,
  open,
  setOpen,
  children,
  setShowPayment,
  setClientSecret,
}) {
  return (
    <Modal
      show={open}
      onClose={() => {
        setOpen(!open);
        setTimeout(() => {
          setShowPayment('');
        }, 1000);
        setClientSecret && setClientSecret('');
      }}
      extraModalClasses="max-w-[528px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="items-start justify-end"
    >
      <div>
        <header className="flex items-center bg-[#F9F9F9] justify-between py-3 px-6 rounded-t-xl border-[0.5px] border-transparent border-b-[#EFEFEF]">
          <h3 className="text-base font-medium text-grey">{headerText}</h3>

          <button
            onClick={() => {
              setOpen(false);
              setTimeout(() => {
                setShowPayment('');
              }, 1000);
              setClientSecret && setClientSecret('');
            }}
            className="bg-[#EDEDED] rounded-md p-1.5"
          >
            <X size={16} color="#242424" />
          </button>
        </header>
        <div className="flex flex-col bg-white">{children}</div>
      </div>
    </Modal>
  );
}

export default connect(state => state, Actions)(TopupModal);
